// Teksty APP: t('…') z polskim tekstem jako kluczem (USTALENIA „Technologia”, SPIS I18-1, I18-2, PLAN-ZROWNANIA
// etap 8). Słownik języka z serwera (`GET /api/jezyki` - worker tłumaczy listę tekstów z buildu po wdrożeniu), kopia
// w pamięci telefonu - start od razu w języku. Tekst bez tłumaczenia (np. nazwa z serwera) zostaje po polsku i idzie
// zbiorczo do `POST /api/jezyki/braki`; ekrany z useJezyk() odświeżają się po dociągnięciu. Język zapisany przy
// koncie (jedno źródło); przed zalogowaniem - ostatni użyty albo język przeglądarki.
// Wstawki: t("Offline od {czas}", { czas }) - w tłumaczeniu zostają na swoich miejscach.
import { useSyncExternalStore } from "react";
import { JEZYKI, type JezykAPP, type LangsReply } from "../gen/kontrakt";
import { adres, naglowki } from "../api/adres"; // nie zaplecze.ts - ono używa t() (bez kręgu importów)

const KLUCZ_JEZYKA = "twf:jezyk";
const klucz_slownika = (j: JezykAPP) => "twf:slownik:" + j;
const KODY = JEZYKI.map(([k]) => k);

function czytaj(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}
function pisz(k: string, v: string): void {
  try { localStorage.setItem(k, v); } catch { /* pamięć niedostępna - słownik i tak przyjdzie z serwera */ }
}

function poczatkowy(): JezykAPP {
  const zapisany = czytaj(KLUCZ_JEZYKA) as JezykAPP | null;
  if (zapisany && KODY.includes(zapisany)) return zapisany;
  for (const l of navigator.languages ?? [navigator.language]) {
    const k = l.slice(0, 2).toLowerCase() as JezykAPP;
    if (KODY.includes(k)) return k;
  }
  return "pl";
}

let jezyk: JezykAPP = poczatkowy();
let slownik: Record<string, string> = JSON.parse(czytaj(klucz_slownika(jezyk)) ?? "{}") as Record<string, string>;
let wersja = 0;
const sluchacze = new Set<() => void>();
const braki = new Set<string>();
let zglaszanie: ReturnType<typeof setTimeout> | null = null;
document.documentElement.lang = jezyk;

function zmieniono(): void {
  wersja++;
  for (const f of sluchacze) f();
}

export function t(klucz: string, wstawki?: Record<string, string | number>): string {
  let s = klucz;
  if (jezyk !== "pl") {
    const tr = slownik[klucz];
    if (tr) s = tr;
    else zglos(klucz);
  }
  if (!wstawki) return s;
  return s.replace(/\{(\w+)\}/g, (cale, nazwa: string) => (nazwa in wstawki ? String(wstawki[nazwa]) : cale));
}

/** Bieżący język APP. */
export const jezykAPP = (): JezykAPP => jezyk;

/** Ekran odświeża się po dociągnięciu tłumaczeń. */
export function useJezyk(): number {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); }, () => wersja);
}

function dopisz(teksty: Record<string, string>): void {
  if (Object.keys(teksty).length === 0) return;
  slownik = { ...slownik, ...teksty };
  pisz(klucz_slownika(jezyk), JSON.stringify(slownik));
  zmieniono();
}

function zglos(klucz: string): void {
  if (!klucz.trim() || braki.has(klucz)) return;
  braki.add(klucz);
  zglaszanie ??= setTimeout(() => void wyslijBraki(), 1500);
}

async function wyslijBraki(): Promise<void> {
  zglaszanie = null;
  const teksty = [...braki].filter((k) => !(k in slownik)).slice(0, 400);
  if (teksty.length === 0 || jezyk === "pl") return;
  try {
    const o = await fetch(adres("/api/jezyki/braki"), { method: "POST",
      headers: await naglowki({ "Content-Type": "application/json" }), body: JSON.stringify({ jezyk, teksty }) });
    if (o.ok) dopisz(((await o.json()) as LangsReply).teksty);
  } catch { /* bez sieci - tekst zostaje po polsku, zgłosi się przy następnym starcie */ }
}

/** Cały słownik języka z serwera (start APP, zmiana języka). */
export async function odswiezSlownik(): Promise<void> {
  if (jezyk === "pl") return;
  try {
    const o = await fetch(adres("/api/jezyki?jezyk=" + jezyk), { headers: await naglowki() });
    if (!o.ok) return;
    slownik = ((await o.json()) as LangsReply).teksty;
    pisz(klucz_slownika(jezyk), JSON.stringify(slownik));
    zmieniono();
  } catch { /* zostaje kopia z pamięci telefonu */ }
}

/** Zmiana języka: słownik z serwera, potem pełne przeładowanie (wszystkie ekrany od razu w nowym języku). */
export async function ustawJezyk(j: JezykAPP, zapiszNaKoncie: boolean): Promise<void> {
  if (zapiszNaKoncie) {
    const o = await fetch(adres("/api/konto/jezyk"), { method: "POST",
      headers: await naglowki({ "Content-Type": "application/json" }), body: JSON.stringify({ jezyk: j }) });
    if (!o.ok) throw new Error("język nie zapisany: " + o.status);
  }
  pisz(KLUCZ_JEZYKA, j);
  if (j === jezyk) return;
  jezyk = j;
  slownik = {};
  await odswiezSlownik();
  location.reload();
}

/** Po zalogowaniu: język konta wygrywa; konto bez języka dostaje bieżący (przeglądarki albo ostatnio użyty). */
export function dopasujJezyk(jezykKonta: JezykAPP | ""): void {
  if (jezykKonta === "") void ustawJezyk(jezyk, true).catch(() => undefined);
  else if (jezykKonta !== jezyk) void ustawJezyk(jezykKonta, false);
}
